/**
 * The doors and windows of a scan, as inspection targets: what KIND each one is,
 * what it is made of and what condition it is in, asked of the room's
 * walkthrough in one model call per opening (`scripts/depth_conditions_pass.py`,
 * target kinds `door` and `window`).
 *
 * Until 2026-10-19 the kind was its own LiDAR-processing pass (`openingTypes`):
 * one opening per SIZE, because every answer cost that room's recording download
 * on top of the call. The inspection downloads every room's recording anyway, so
 * the per-size saving is gone and every door and window is asked about on its
 * own. Sharing still happens, on the APPLY side: an opening nobody could see
 * borrows the kind from a measured one of the same size
 * (`shared-lib/existing-conditions/openingTypeApply.js`), on the old premise that a
 * house buys its units in packs.
 *
 * Pure. The aim rules are the old pass's, measured there
 * (`reference-docs/lidar-recon/opening-types.md`):
 *
 * - A DOOR's aim is a VOLUME reaching a leaf's width into the room: the evidence
 *   for a swing is a leaf standing out at ninety degrees, outside the opening's
 *   own rectangle, and for a pocket that no leaf stands anywhere. A flat outline
 *   shows the same empty hole for both.
 * - A WINDOW's aim is the opening itself, flat on the wall. The hull of anything
 *   deeper takes in the room around it: on 19A7B370 it swallowed the TV on the
 *   next wall and the model described "a mirror".
 * - Both carry a `face` (the wall plane and its normal toward the host room), so
 *   the frame picker rejects a camera standing on the other side of the wall.
 * - The jambs are DRAWN and lettered A and B, and the target records which world
 *   end each letter is on, so an answer about a jamb needs no camera.
 */
const { isPointInsidePolygon } = require('../geometry/pointInPolygon');
const { wallInwardNormalXZ } = require('../geometry/wallGeometry');
const { polygonOf } = require('./roomOutline');

/** The two kinds of opening the inspection asks about, and the type question of each. */
const CLAIM_DOOR_TYPE = 'door-type';
const CLAIM_WINDOW_STYLE = 'window-style';
const CLAIM_FOR = { door: CLAIM_DOOR_TYPE, window: CLAIM_WINDOW_STYLE };

/**
 * How far into the room a door's probe reaches, as a multiple of its own width: a
 * leaf standing at 90 degrees occupies a square whose side IS the opening width.
 * Clamped so a 2.4 m slider does not outline half the room and a 0.6 m closet
 * door still shows the floor its leaf would sweep.
 */
const LEAF_REACH_MIN_M = 0.55;
const LEAF_REACH_MAX_M = 1.2;

/** Margin around the opening, so the outline contains jamb, casing and sill. */
const CASING_MARGIN_M = 0.08;

/** An opening under this wide or high is hardware, not a door or a window. */
const MIN_OPENING_M = 0.3;

/**
 * Two openings are the same PRODUCT within this much, and so may share a kind.
 *
 * 0.1 m is the gap between the standard leaf widths a house actually mixes —
 * 28", 30", 32" and 36" are 0.051 m apart at the tightest, so a tolerance this
 * size merges the two that differ by two inches and keeps 30" apart from 36".
 * Height is looser because a door's own height barely varies while a WINDOW's
 * head height moves with its sill.
 */
const SAME_PRODUCT_WIDTH_TOL_M = 0.1;
const SAME_PRODUCT_HEIGHT_TOL_M = 0.15;

/** Same opening within capture noise (a reprocess moves an opening a little). */
const SAME_OPENING_XZ_TOL_M = 0.25;
const SAME_OPENING_Y_TOL_M = 0.4;
const SAME_OPENING_SIZE_TOL_M = 0.3;

const round = (v) => +(Number(v) || 0).toFixed(4);

/** An opening's `rotation.y`, rounded as every probe key here rounds it. */
const probeRotationY = (o) => {
  const y = (o && o.rotation && o.rotation.y) ?? 0;
  return Number.isFinite(y) ? +y.toFixed(4) : 0;
};

/**
 * The geometric identity of an opening, which is what an answer is matched back
 * on. Ids are re-minted on every reprocess; centre, angle and size survive one
 * because they came from the capture.
 */
function openingKey(opening) {
  const c = (opening && opening.center) || {};
  const d = (opening && opening.dimensions) || {};
  return {
    center: { x: round(c.x), y: round(c.y), z: round(c.z) },
    rotationY: probeRotationY(opening),
    widthM: round(d.x),
    heightM: round(d.y),
  };
}

/** Is `opening` the one `key` was taken from, within capture noise? */
function matchesOpening(key, opening) {
  if (!key || !key.center) return false;
  const k = openingKey(opening);
  return (
    Math.hypot(key.center.x - k.center.x, key.center.z - k.center.z) <=
      SAME_OPENING_XZ_TOL_M &&
    Math.abs((key.center.y ?? 0) - k.center.y) <= SAME_OPENING_Y_TOL_M &&
    Math.abs((key.widthM ?? 0) - k.widthM) <= SAME_OPENING_SIZE_TOL_M &&
    Math.abs((key.heightM ?? 0) - k.heightM) <= SAME_OPENING_SIZE_TOL_M
  );
}

/** Do these two openings come off the same shelf? `{ widthM, heightM }` each. */
function sameProduct(a, b) {
  return (
    Math.abs((a.widthM ?? 0) - (b.widthM ?? 0)) <= SAME_PRODUCT_WIDTH_TOL_M &&
    Math.abs((a.heightM ?? 0) - (b.heightM ?? 0)) <= SAME_PRODUCT_HEIGHT_TOL_M
  );
}

/** The opening's two plan ends, lettered, in the wall's own direction. */
function jambEnds(opening) {
  const c = opening.center || {};
  const ry = (opening.rotation && opening.rotation.y) || 0;
  // Local +X maps to (cos ry, -sin ry) — the three.js Y-Euler convention every
  // stored `rotation.y` here follows, and the one `box_corners` reads.
  const t = { x: Math.cos(ry), z: -Math.sin(ry) };
  const half = ((opening.dimensions && opening.dimensions.x) || 0) / 2;
  return {
    A: { x: round(c.x - t.x * half), z: round(c.z - t.z * half) },
    B: { x: round(c.x + t.x * half), z: round(c.z + t.z * half) },
    tangent: { x: round(t.x), z: round(t.z) },
  };
}

/**
 * The aim for one opening, seen from one room: a volume for a door, the opening
 * itself for a window (see the header).
 *
 * The extrusion runs along local +Z, which `box_corners` maps to
 * `(sin ry, cos ry)`, so the inward normal decides the SIGN of the offset.
 * Getting that backwards points the probe into the neighbouring room, where the
 * camera never stands: the failure is a silent `never-seen`, not a wrong answer.
 */
function aimVolume(opening, inwardNormal, kind = 'door') {
  const c = opening.center || {};
  const d = opening.dimensions || {};
  const width = d.x || 0;
  const reach =
    kind === 'window'
      ? 0
      : Math.min(LEAF_REACH_MAX_M, Math.max(LEAF_REACH_MIN_M, width));
  const ry = (opening.rotation && opening.rotation.y) || 0;
  const localZ = { x: Math.sin(ry), z: Math.cos(ry) };
  const sign =
    localZ.x * inwardNormal.x + localZ.z * inwardNormal.z >= 0 ? 1 : -1;
  const offset = (sign * reach) / 2;
  return {
    center: {
      x: round(c.x + localZ.x * offset),
      y: round(c.y),
      z: round(c.z + localZ.z * offset),
    },
    dimensions: {
      x: round(width + 2 * CASING_MARGIN_M),
      y: round((d.y || 0) + 2 * CASING_MARGIN_M),
      z: round(reach),
    },
    rotationY: probeRotationY(opening),
    type: kind,
    // The wall plane itself, for the side gate and the incidence weight. A door
    // volume's own centre stands half a reach off the wall, so it cannot serve.
    face: {
      normal: { x: round(inwardNormal.x), z: round(inwardNormal.z) },
      center: { x: round(c.x), y: round(c.y), z: round(c.z) },
    },
  };
}

/** Plan area of a room's floor, for ranking which side of a wall to shoot from. */
function floorAreaOf(room) {
  const poly = polygonOf(room) || [];
  if (poly.length < 3) return 0;
  let acc = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    acc += p.x * q.z - q.x * p.z;
  }
  return Math.abs(acc) / 2;
}

/**
 * The room-wall copy's normal pointing INTO `room`: the wall's own, flipped when a
 * step along it from the OPENING leaves the room's polygon. Probed from the
 * opening rather than the wall's middle: a long wall's middle can sit beside
 * another room's floor (an L-shaped room, a wall shared along a hall).
 */
function inwardNormalIn(room, wall, point) {
  const n = wallInwardNormalXZ(wall);
  const poly = polygonOf(room);
  if (!poly || poly.length < 3 || !point) return n;
  const probe = { x: point.x + n.x * 0.2, z: point.z + n.z * 0.2 };
  return isPointInsidePolygon(probe, poly) ? n : { x: -n.x, z: -n.z };
}

/** How far past a room wall's end an opening's centre may sit and still be on it. */
const ON_WALL_SLACK_M = 0.1;

/** How far off a room wall's line an opening's centre may sit and still be in it. */
const ON_WALL_OFFSET_M = 0.5;

/**
 * How far this room-wall copy is from the opening, across its line — or null when
 * its own stretch does not reach the opening. A structure wall can run past
 * several rooms (a hall and the bathroom beside it share one long partition), and
 * one room can carry several pieces of it: on 19A7B370 the living room's east wall
 * is a 7.4 m piece plus an 11 cm jog at its end running the other way, the jog
 * listed first. Naming the structure wall is not enough, and neither is reaching
 * along a line — the opening must also sit ON it.
 */
function offsetFrom(wall, point) {
  const { bottomLeft: a, bottomRight: b } = wall.alignment;
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dz);
  if (!(len > 0)) return null;
  const along = ((point.x - a.x) * dx + (point.z - a.z) * dz) / len;
  if (along < -ON_WALL_SLACK_M || along > len + ON_WALL_SLACK_M) return null;
  const across = Math.abs((point.x - a.x) * dz - (point.z - a.z) * dx) / len;
  return across <= ON_WALL_OFFSET_M ? across : null;
}

/**
 * Every room whose own wall on this structure wall reaches the opening and that
 * can be inspected (it has a `roomIndex`, which names its recording, and a
 * floor), largest first: the camera has to stand back far enough to hold a 2 m
 * door in frame, and the small side of a partition is routinely a closet shot
 * from inside its own doorway.
 */
function hostsFor(rooms, structureWallId, point) {
  const out = [];
  for (const room of rooms || []) {
    if (!Number.isInteger(room.roomIndex)) continue;
    // the room's piece of this structure wall that the opening sits on, nearest first
    let wall = null;
    let best = Infinity;
    for (const w of room.walls || []) {
      if (
        !w ||
        !w.alignment ||
        w.alignment.structureWallId !== structureWallId ||
        !w.alignment.bottomLeft ||
        !w.alignment.bottomRight
      )
        continue;
      const off = offsetFrom(w, point);
      if (off !== null && off < best) {
        best = off;
        wall = w;
      }
    }
    if (!wall) continue;
    const area = floorAreaOf(room);
    if (!(area > 0)) continue;
    out.push({ room, normal: inwardNormalIn(room, wall, point), area });
  }
  return out.sort((a, b) => b.area - a.area);
}

/** What the structure currently says the opening is, so an answer that agrees reads as one. */
function currentTypeOf(opening, kind) {
  const c = opening.category || {};
  return kind === 'door'
    ? (c.door && c.door.doorType) || null
    : (c.window && c.window.style) || null;
}

/** Two doors whose edges are this close along one wall are one unit (a split pair). */
const ONE_UNIT_GAP_M = 0.15;

/** ...and turned the same way, within this (radians, either facing). */
const ONE_UNIT_ANGLE_RAD = 0.05;

/** An opening's span along its own direction: `[lo, hi]` about the first one's centre. */
function spanAlong(opening, origin, tangent) {
  const c = opening.center;
  const t = (c.x - origin.x) * tangent.x + (c.z - origin.z) * tangent.z;
  const half = (opening.dimensions.x || 0) / 2;
  return [t - half, t + half];
}

/**
 * The doors of one wall that touch, as units: each a list of one or more doors,
 * in order along the wall. A pair of doors under one casing — two pocket doors, a
 * French pair — is often recorded by RoomPlan as two doors side by side, and asked
 * about one half at a time the model reads the other half's shut panel, standing
 * beside the opening, as an open leaf (19A7B370's living-room pocket pair, called
 * hinged on both halves). So a unit is asked about whole. Windows are not joined.
 */
function doorUnits(doors) {
  const byWall = new Map();
  for (const d of doors) {
    const key = (d.parentWallIds || [])[0] || `none:${d.id}`;
    if (!byWall.has(key)) byWall.set(key, []);
    byWall.get(key).push(d);
  }
  const units = [];
  for (const list of byWall.values()) {
    const first = list[0];
    const ry = (first.rotation && first.rotation.y) || 0;
    const tangent = { x: Math.cos(ry), z: -Math.sin(ry) };
    const sorted = list
      .map((d) => ({ d, span: spanAlong(d, first.center, tangent) }))
      .sort((a, b) => a.span[0] - b.span[0]);
    let unit = null;
    for (const e of sorted) {
      const last = unit && unit[unit.length - 1];
      const turn = last
        ? Math.abs(
            Math.sin(
              ((e.d.rotation && e.d.rotation.y) || 0) -
                ((last.d.rotation && last.d.rotation.y) || 0)
            )
          )
        : 0;
      if (
        last &&
        e.span[0] - last.span[1] <= ONE_UNIT_GAP_M &&
        turn <= ONE_UNIT_ANGLE_RAD
      ) {
        unit.push(e);
      } else {
        unit = [e];
        units.push(unit);
      }
    }
  }
  return units.map((u) => u.map((e) => e.d));
}

/**
 * One opening standing for a unit of doors: their joint span along the wall, the
 * tallest height, the first door's turn. What the unit is aimed at and keyed by.
 */
function unitOpening(members) {
  if (members.length === 1) return members[0];
  const first = members[0];
  const ry = (first.rotation && first.rotation.y) || 0;
  const tangent = { x: Math.cos(ry), z: -Math.sin(ry) };
  const spans = members.map((d) => spanAlong(d, first.center, tangent));
  const lo = Math.min(...spans.map((s) => s[0]));
  const hi = Math.max(...spans.map((s) => s[1]));
  const mid = (lo + hi) / 2;
  const height = Math.max(...members.map((d) => d.dimensions.y || 0));
  const ys = members.map((d) => d.center.y).filter(Number.isFinite);
  return {
    id: first.id,
    center: {
      x: first.center.x + tangent.x * mid,
      y: ys.length ? ys.reduce((a, b) => a + b, 0) / ys.length : first.center.y,
      z: first.center.z + tangent.z * mid,
    },
    dimensions: { x: hi - lo, y: height, z: first.dimensions.z || 0 },
    rotation: { ...(first.rotation || {}), y: ry },
    parentWallIds: first.parentWallIds,
    category: first.category,
  };
}

/**
 * One target per door UNIT and per window of the structure (see `doorUnits`),
 * aimed from the largest room that backs its wall with the other rooms behind it
 * as `alternates`, and the ones that could not be aimed (`skipped`). A unit of
 * several doors carries each door's key in `anchor.members`, in order along the
 * wall, so its one answer is spread back onto every door.
 *
 * Returned WITHOUT the room fields (`id`, `roomId`, `roomIndex`, …): the caller
 * stamps them, as it does for every other target, so an opening target is the
 * same shape as a wall face's. `room` names the host.
 *
 * Cased openings (`structure.openings`) are not asked about: there is no leaf or
 * sash to type, and the wall around them is the wall face's.
 */
function openingTargets({ rooms, structure }) {
  const found = [];
  const skipped = [];
  const usable = (o) =>
    o &&
    o.center &&
    o.dimensions &&
    o.dimensions.x > MIN_OPENING_M &&
    o.dimensions.y > MIN_OPENING_M;
  const units = [
    ...doorUnits(((structure && structure.doors) || []).filter(usable)).map(
      (members) => ['door', members]
    ),
    ...((structure && structure.windows) || [])
      .filter(usable)
      .map((w) => ['window', [w]]),
  ];
  for (const [kind, members] of units) {
    const opening = unitOpening(members);
    const wallId = (opening.parentWallIds || [])[0];
    const [host, ...others] = wallId
      ? hostsFor(rooms, wallId, opening.center)
      : [];
    if (!host) {
      for (const m of members)
        skipped.push({ openingId: m.id, kind, reason: 'no-host-room' });
      continue;
    }
    const ends = jambEnds(opening);
    found.push({
      room: host.room,
      target: {
        kind,
        elementId: opening.id,
        claim: CLAIM_FOR[kind],
        currentType: currentTypeOf(opening, kind),
        ...(members.length > 1 ? { memberIds: members.map((m) => m.id) } : {}),
        anchor: {
          openingKind: kind,
          openingKey: openingKey(opening),
          // the doors this one question stands for, each by its own key
          ...(members.length > 1 ? { members: members.map(openingKey) } : {}),
          faceNormal: {
            x: round(host.normal.x),
            z: round(host.normal.z),
          },
          // The two jambs, NAMED. The model answers `A` or `B` about a jamb;
          // these say where each letter stands in the world.
          jambs: { A: ends.A, B: ends.B, tangent: ends.tangent },
        },
        aim: aimVolume(opening, host.normal, kind),
        // The rooms on the other side of the wall, largest first: the pass tries
        // the next one's recording when this room could not get a look at the
        // opening (no recording, never in view, every frame refused), and files
        // the answer under this room. Not in the id hash (it is not the anchor).
        ...(others.length
          ? {
              alternates: others.map((h) => ({
                roomId: h.room.id,
                roomIndex: h.room.roomIndex,
                roomName: h.room.name || null,
                faceNormal: { x: round(h.normal.x), z: round(h.normal.z) },
                aim: aimVolume(opening, h.normal, kind),
              })),
            }
          : {}),
      },
    });
  }
  return { found, skipped };
}

module.exports = {
  CLAIM_DOOR_TYPE,
  CLAIM_WINDOW_STYLE,
  CLAIM_FOR,
  LEAF_REACH_MIN_M,
  LEAF_REACH_MAX_M,
  MIN_OPENING_M,
  SAME_PRODUCT_WIDTH_TOL_M,
  SAME_PRODUCT_HEIGHT_TOL_M,
  openingKey,
  matchesOpening,
  sameProduct,
  jambEnds,
  aimVolume,
  hostsFor,
  doorUnits,
  openingTargets,
};
